import * as React from "react";
import Modal from "@mui/material/Modal";
import { Box, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Form, Formik } from "formik";
import theme from "../../../theme";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { StyledBox, StyledBoxFlex } from "../success-virtual-locker/style";
import CrossIcon from "../../../assets/Icons/CrossIcon";
import CustomInput from "../../common/input/CustomInput";
import CustomDropdown from "../../common/common-dropdown/CustomDropdown";
import { StyledBoxCard } from "../setting-coupon/style";
import { StyledTypography } from "../manual-split-calculation/style";
import {
  SHOPPER_PRICE_ADD,
  SHOPPER_PRICE_EDIT,
  SHOPPER_PRICE_LIST,
} from "../../../actions/admin/customer-actions";

// Yup Validation Schema
const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be at most 50 characters"),

  price: Yup.number()
    .required("Price is required")
    .positive("Price must be positive"),

  region: Yup.string().required("Region is required"),

  status: Yup.string().required("Status is required"),
});

export default function SettingShopperPrice({
  open,
  handleClose,
  isEditMode,
  editRecord,
}) {
  const dispatch = useDispatch();
  const [initialValue, setInitialValue] = React.useState({
    name: "",
    price: "",
    region: "",
    status: "",
  });

  const getShopperPriceList = React.useCallback(
    (values) => {
      dispatch({
        type: SHOPPER_PRICE_LIST,
        payload: values,
      });
    },
    [dispatch]
  );

  React.useEffect(() => {
    if (isEditMode) {
      setInitialValue({
        name: editRecord?.name || "",
        price: editRecord?.price || "",
        region: editRecord?.region || "",
        status: editRecord?.status || "",
      });
    }
  }, [isEditMode, editRecord]);

  return (
    <Modal open={open} onClose={handleClose}>
      <StyledBox>
        <StyledBoxCard>
          <StyledBoxFlex>
            <StyledTypography>
              {isEditMode ? "Edit Shopper Pricing" : "Add Shopper Pricing"}
            </StyledTypography>
          </StyledBoxFlex>
          <CrossIcon color={theme.palette.custom.white} onClick={handleClose} />
        </StyledBoxCard>

        <Formik
          initialValues={initialValue}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values) => {
            if (isEditMode && editRecord.id) {
              dispatch({
                type: SHOPPER_PRICE_EDIT,
                payload: {
                  ...values,
                  id: editRecord.id,
                  onSuccess: () => {
                    getShopperPriceList({});
                    handleClose();
                  },
                },
              });
            } else {
              dispatch({
                type: SHOPPER_PRICE_ADD,
                payload: {
                  ...values,
                  onSuccess: () => {
                    getShopperPriceList({});
                    handleClose();
                  },
                },
              });
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {/* Name */}
                  <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <CustomInput
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      height="10px"
                      // required
                      placeholder="Name"
                      customLabel="Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>

                  {/* Price */}
                  <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <CustomInput
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      height="10px"
                      // required
                      type="number"
                      placeholder="Price"
                      customLabel="Price"
                      name="price"
                      value={values.price}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.price && Boolean(errors.price)}
                      helperText={touched.price && errors.price}
                    />
                  </Grid>

                  {/* Region */}
                  <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <CustomInput
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      height="10px"
                      // required
                      placeholder="Region"
                      customLabel="Region"
                      name="region"
                      value={values.region}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.region && Boolean(errors.region)}
                      helperText={touched.region && errors.region}
                    />
                  </Grid>

                  {/* Status */}
                  <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
                    <CustomDropdown
                      options={[
                        { label: "Active", value: "active" },
                        { label: "Draft", value: "draft" },
                      ]}
                      color={theme.palette.custom.productTextColor}
                      fontSize={theme.typography.regular.fontSize}
                      height="44px"
                      required
                      customLabel="Status"
                      name="status"
                      value={values?.status}
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      error={touched.status && Boolean(errors.status)}
                      helperText={touched.status && errors.status}
                    />
                  </Grid>

                  {/* Submit */}
                  <Grid container justifyContent="space-between" width="100%">
                    <Grid item>
                      <Button variant="outlined" onClick={handleClose}>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button variant="contained" color="primary" type="submit">
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
      </StyledBox>
    </Modal>
  );
}
